import { CaretLeft } from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { BetaTag } from './BetaTag';
import { StabilityLevel } from 'src/types/stability-level';
import { ExperimentalTag } from './ExperimentalTag';

type HeadingProps = {
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  backAction?: () => void;
  stabilityLevel?: StabilityLevel;
  fixedHeader?: boolean;
};

const TitleWrapper = ({ children }: { children: React.ReactNode }) => {
  return React.isValidElement(children) ? children : <h1>{children}</h1>;
};

export const Heading = ({
  title,
  subtitle,
  children,
  className,
  backAction,
  stabilityLevel = StabilityLevel.Stable,
  fixedHeader = false,
}: HeadingProps) => {
  const [isFloating, setIsFloating] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fixedHeader || !headerRef.current || !childrenRef.current) return;

    const handleScroll = () => {
      const headerRect = headerRef.current?.getBoundingClientRect();
      if (!headerRect) return;

      setIsFloating(headerRect.bottom < 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [fixedHeader]);

  return (
    <div
      className={clsx('p-6 flex flex-col w-full gap-2', 'border-b', className)}
    >
      <div
        ref={headerRef}
        className="flex flex-row md:flex-row justify-between"
      >
        <div className="flex gap-x-2 w-full md:w-auto">
          {backAction && (
            <div
              className="cursor-pointer text-neutral-400 mt-2"
              onClick={backAction}
            >
              <CaretLeft size={20} />
            </div>
          )}
          <div className="flex flex-row items-center gap-x-3 w-full">
            <div className="flex items-center gap-x-2">
              <TitleWrapper>{title}</TitleWrapper>
              {stabilityLevel === StabilityLevel.Beta && (
                <BetaTag size="small" />
              )}
            </div>
            <span className="text-neutral-600 max-w-[800px] max-h-[100px] overflow-hidden text-ellipsis">
              {!!subtitle && subtitle}
            </span>
          </div>
        </div>
        <div
          ref={childrenRef}
          className="flex flex-col min-w-[160px] mb-2 md:mb-0 items-end"
        >
          {children}
        </div>
      </div>
      {stabilityLevel === StabilityLevel.Experimental && (
        <div className="flex items-center gap-x-2 text-sm">
          <ExperimentalTag size="small" /> We'd love your feedback!
        </div>
      )}
      {fixedHeader && isFloating && (
        <div
          className={clsx(
            'hidden md:flex fixed top-0 left-0 right-0 z-50 bg-neutral-0 border-b border-neutral-100',
            'transition-all duration-200 ease-in-out',
            'flex justify-between items-center p-4 w-[98vw] ml-4',
          )}
          style={{
            transform: 'translateY(0)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <img src="/assets/logo.svg" alt="Logo" className="h-8" />
          <div className="flex flex-col min-w-[160px]">{children}</div>
        </div>
      )}
    </div>
  );
};
